import type { FinancePartnerRow } from "../store/oneButtonStore";

const OPP_PRODUCT_ORDER = ["Solar","Roof","Battery","Solar + Roof","Battery + Roof","Solar + Battery","RR","RRR",];

export function primarySavedFinanceFromOpportunity(opportunityDetails: any): { partner: string; method: string } | null {
    const raw = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
    const arr = Array.isArray(raw) ? raw : [];
    const active = arr.filter((p: any) => p.ActiveFlag !== false && p.activeFlag !== false);

    if (!active.length) return null;

    active.sort((a: any, b: any) => {
        const na = String(a.Product_Type_Name ?? a.product_Type_Name ?? "");
        const nb = String(b.Product_Type_Name ?? b.product_Type_Name ?? "");
        const ia = OPP_PRODUCT_ORDER.indexOf(na);
        const ib = OPP_PRODUCT_ORDER.indexOf(nb);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });

    const p = active[0];
    const partner = String(p.Finance_Partner_Display_Name ??p.FinancePartnerDisplayName ??p.Finance_Partner ??"").trim();
    const method = String(p.Finance_Method_Name ?? p.FinanceMethod ?? "").trim();

    if (!partner || /select finance/i.test(partner)) return null;
    return { partner, method: method || "—" };
}

/** Active rows: treat missing ActiveFlag as active (same as `primarySavedFinanceFromOpportunity`). */
function opportunityProductActiveRows(opportunityDetails: any | null | undefined): any[] {
    const raw = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
    const arr = Array.isArray(raw) ? raw : [];
    return arr.filter((p: any) => p.ActiveFlag !== false && p.activeFlag !== false);
}

function productTypeName(p: any): string {
    return String(p?.Product_Type_Name ?? p?.product_Type_Name ?? "").trim();
}

/** Active opportunity product type names (e.g. Solar, Roof) for Pricing API `products`. */
export function activeOpportunityProductTypeNames(opportunityDetails: any | null | undefined): string[] {
    return opportunityProductActiveRows(opportunityDetails).map(productTypeName).filter(Boolean);
}

/** Product types flagged on the opportunity interest (may not be saved/linked yet). */
export function opportunityInterestProductTypeNames(opportunityDetails: any | null | undefined): string[] {
    const interest = opportunityDetails?.OpportunityInterest ?? opportunityDetails?.opportunityInterest ?? {};
    const out: string[] = [];
    if (interest.Solar) out.push('Solar');
    if (interest.Roof || interest.RoofOnly) out.push('Roof');
    if (interest.Battery || interest.BatteryOnly) out.push('Battery');
    if (interest.RRR) out.push('RRR');
    if (interest.RR) out.push('RR');
    return out;
}

function sortOpportunityProductTypes(names: string[]): string[] {
    return [...names].sort((a, b) => {
        const ia = OPP_PRODUCT_ORDER.indexOf(a);
        const ib = OPP_PRODUCT_ORDER.indexOf(b);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
}

/**
 * Product lines sent to the Pricing API. Merges saved active products with opportunity interest,
 * and includes Roof + Battery for solar deals so default design can price before finance is linked.
 */
export function pricingProductTypeNames(opportunityDetails: any | null | undefined): string[] {
    const set = new Set<string>(activeOpportunityProductTypeNames(opportunityDetails));
    for (const name of opportunityInterestProductTypeNames(opportunityDetails)) {
        set.add(name);
    }
    if (set.has('Solar') || set.size === 0) {
        set.add('Roof');
        set.add('Battery');
    }
    return sortOpportunityProductTypes([...set]);
}

export type PricingToggleProduct = 'Solar' | 'Roof' | 'Battery';

export const DEFAULT_PRICING_PRODUCTS_ENABLED: Record<PricingToggleProduct, boolean> = {
    Solar: false,
    Roof: false,
    Battery: false,
};

export function hasAnyPricingProductEnabled(
    enabled: Record<PricingToggleProduct, boolean>,
): boolean {
    return enabled.Solar || enabled.Roof || enabled.Battery;
}

/** Map system-design card kind to a pricing toggle key (solar / roof / battery cards only). */
export function cardKindToPricingToggleProduct(kind: string): PricingToggleProduct | null {
    if (kind === 'solar') return 'Solar';
    if (kind === 'roof') return 'Roof';
    if (kind === 'battery') return 'Battery';
    return null;
}

/** Filter Pricing API product lines by per-product include toggles (compound lines need all parts enabled). */
export function filterPricingProductsForApi(
    products: string[],
    enabled: Record<PricingToggleProduct, boolean>,
): string[] {
    return products.filter((name) => {
        const lower = name.toLowerCase();
        const needsSolar = lower.includes('solar');
        const needsRoof = lower.includes('roof') || lower === 'rr' || lower === 'rrr';
        const needsBattery = lower.includes('battery');
        if (needsSolar && !enabled.Solar) return false;
        if (needsRoof && !enabled.Roof) return false;
        if (needsBattery && !enabled.Battery) return false;
        return true;
    });
}

/** True when Pricing API payload `products` includes a solar product type. */
export function pricingPayloadRequiresSolarMetrics(payload: { products?: unknown }): boolean {
    const products = payload?.products;
    if (!Array.isArray(products) || products.length === 0) return false;
    return products.some((p) => {
        const lower = String(p).toLowerCase().replace(/\s+/g, "");
        return lower === "solar" || lower === "s" || lower.startsWith("solar");
    });
}

/**
 * True when every active `OpportunityProduct` row is Roof (no Solar / Battery / etc. on the opportunity).
 * Used to hide or disable utility editing for roof-only opportunities.
 */
export function isOpportunityActiveProductsRoofOnly(opportunityDetails: any | null | undefined): boolean {
    const active = opportunityProductActiveRows(opportunityDetails);
    if (!active.length) return false;
    return active.every((p) => productTypeName(p) === "Roof");
}

export function getFinanceSummaryDisplay(opportunityDetails: any | null,selectedFinanceCells: Set<string>,financingGrid: FinancePartnerRow[]): { 
    partner: string; method: string } | null {
    const saved = primarySavedFinanceFromOpportunity(opportunityDetails);
    if (saved) return saved;

    const key = Array.from(selectedFinanceCells)[0];
    if (!key) return null;

    const [partner, product] = key.split("::");
    const row = financingGrid.find((r) => r.partner === partner);
    const cell = row?.prices[product];
    const method = (cell?.financeMethod || "").trim() || "—";

    return { partner, method };
}
